import { FormName } from '@core/data-access/models/form/form.model';
import { PersonType } from '@core/data-access/models/person.model';
import { FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import {
  setSavedTicketHolderFormFields,
  setSavedTicketHolderFormFieldsWithoutSaving
} from '@core/data-access/store/form/form.reducer';
import { disableAddressFormField, setDisabledFormFields } from '@core/utils/form-fields/form-field-helpers';
import { createReducer, on } from '@ngrx/store';
import { cloneDeep, find, forEach, isEmpty, isNil, mapValues, merge, omitBy, pickBy, reduce } from 'lodash';
import { SavedTicketHoldersStoreState } from '../models/saved-ticket-holders.store.model';
import {
  CreateTicketHolderFormActions,
  SavedTicketHolderActions,
  SavedTicketHolderFormActions
} from './saved-ticket-holder.actions';

export const savedTicketHoldersFeatureKey = 'savedTicketHolders';

export const initialSavedTicketsHolderState: SavedTicketHoldersStoreState = {
  savedTicketHolders: {},
  forms: {
    [FormName.SavedTicketHolders]: {},
    [FormName.CreateTicketHolder]: {
      formFields: {}
    }
  }
};

export const savedTicketHoldersReducer = createReducer(
  initialSavedTicketsHolderState,
  on(SavedTicketHolderActions.setSavedTicketHolders, (state, { savedTicketHolders }) => {
    if (isEmpty(savedTicketHolders)) {
      return state;
    }

    const { forms } = state;

    const updatedSavedTicketHolders = cloneDeep(state.savedTicketHolders);
    const updatedSavedTicketHolderForms = cloneDeep(state.forms[FormName.SavedTicketHolders]);

    forEach(savedTicketHolders, (savedTicketHolder) => {
      const { person: savedPerson, formFields: savedFormFields } = savedTicketHolder;

      const currentPersonSavedTicketHolder = find(
        updatedSavedTicketHolders,
        ({ person }) => person.id === savedPerson.id
      );

      if (isNil(currentPersonSavedTicketHolder)) {
        updatedSavedTicketHolders[savedPerson.uuid] = {
          person: savedPerson,
          formFields: disableAddressFormField(setDisabledFormFields(savedPerson, savedFormFields))
        };
      } else {
        const { person, formFields } = currentPersonSavedTicketHolder;

        const currentUnsavedFormFields = omitBy(formFields, (formField) => formField?.isSaved);

        updatedSavedTicketHolders[person.uuid] = {
          person,
          formFields: {
            ...disableAddressFormField(setDisabledFormFields(person, savedFormFields)),
            ...currentUnsavedFormFields
          }
        };
      }

      const currentPersonSavedTicketHolderForm = find(
        updatedSavedTicketHolderForms,
        ({ person }) => person.id === savedPerson.id
      );

      if (isNil(currentPersonSavedTicketHolderForm)) {
        updatedSavedTicketHolderForms[savedPerson.uuid] = {
          person: savedPerson,
          formFields: savedFormFields
        };
      } else {
        const { person, formFields } = currentPersonSavedTicketHolderForm;

        const currentUnsavedFormFields = omitBy(formFields, (formField) => formField?.isSaved);

        updatedSavedTicketHolderForms[person.uuid] = {
          person,
          formFields: {
            ...savedFormFields,
            ...currentUnsavedFormFields
          }
        };
      }
    });

    return {
      ...state,
      savedTicketHolders: updatedSavedTicketHolders,
      forms: {
        ...forms,
        [FormName.SavedTicketHolders]: updatedSavedTicketHolderForms
      }
    };
  }),
  on(SavedTicketHolderActions.addCreatedTicketHolders, (state, { createdTicketHolders }) => {
    if (isEmpty(createdTicketHolders)) {
      return state;
    }

    const { forms } = state;

    const disabledCreatedTicketHoldersFormFields = mapValues(createdTicketHolders, ({ person, formFields }) => ({
      person,
      formFields: disableAddressFormField(setDisabledFormFields(person, formFields))
    }));

    return {
      ...state,
      savedTicketHolders: {
        ...state.savedTicketHolders,
        ...disabledCreatedTicketHoldersFormFields
      },
      forms: {
        ...forms,
        [FormName.SavedTicketHolders]: {
          ...forms[FormName.SavedTicketHolders],
          ...createdTicketHolders
        },
        [FormName.CreateTicketHolder]: {
          formFields: {}
        }
      }
    };
  }),
  on(SavedTicketHolderActions.addCreatedTicketHoldersBeforeSaveOrder, (state, { createdTicketHolders }) => {
    if (isEmpty(createdTicketHolders)) {
      return state;
    }

    const disabledCreatedTicketHoldersFormFields = mapValues(createdTicketHolders, ({ person, formFields }) => ({
      person,
      formFields: disableAddressFormField(setDisabledFormFields(person, formFields))
    }));

    const isBuyerCreated = find(createdTicketHolders, ({ person }) => person.type === PersonType.Buyer);

    const savedTicketHolders = cloneDeep(state.savedTicketHolders);
    const updatedExistingSavedTicketHolders = isBuyerCreated
      ? pickBy(savedTicketHolders, ({ person }) => person.type !== PersonType.Buyer)
      : savedTicketHolders;

    const savedTicketHolderForms = cloneDeep(state.forms[FormName.SavedTicketHolders]);
    const updatedExistingSavedTicketHolderForms = isBuyerCreated
      ? pickBy(savedTicketHolderForms, ({ person }) => person.type !== PersonType.Buyer)
      : savedTicketHolderForms;

    return {
      ...state,
      savedTicketHolders: {
        ...updatedExistingSavedTicketHolders,
        ...disabledCreatedTicketHoldersFormFields
      },
      forms: {
        ...state.forms,
        [FormName.SavedTicketHolders]: {
          ...updatedExistingSavedTicketHolderForms,
          ...createdTicketHolders
        }
      }
    };
  }),
  on(SavedTicketHolderActions.setUpdatedTicketHolder, (state, { person, formFields }) => {
    if (isNil(person) || isEmpty(formFields)) {
      return state;
    }

    const mergedFormFields = merge({}, state.savedTicketHolders[person.uuid].formFields, formFields);
    const mergedAndDisabledFormFields = setDisabledFormFields(person, mergedFormFields);
    const disabledFormFields = disableAddressFormField(mergedAndDisabledFormFields);

    return {
      ...state,
      savedTicketHolders: {
        ...state.savedTicketHolders,
        [person.uuid]: {
          person,
          formFields: disabledFormFields
        }
      },
      forms: {
        ...state.forms,
        [FormName.SavedTicketHolders]: {
          ...state.forms[FormName.SavedTicketHolders],
          [person.uuid]: {
            person,
            formFields: merge({}, state.forms[FormName.SavedTicketHolders][person.uuid].formFields, formFields)
          }
        }
      }
    };
  }),
  on(SavedTicketHolderActions.removeDeletedTicketHolder, (state, { person }) => {
    if (isNil(person)) {
      return state;
    }

    return {
      ...state,
      savedTicketHolders: pickBy(state.savedTicketHolders, ({ person: { id } }) => id !== person.id),
      forms: {
        ...state.forms,
        [FormName.SavedTicketHolders]: pickBy(
          state.forms[FormName.SavedTicketHolders],
          ({ person: { id } }) => id !== person.id
        )
      }
    };
  }),
  on(SavedTicketHolderFormActions.setSavedTicketHolderFormFieldsWithoutSaving, (state, { person, formFields }) =>
    setSavedTicketHolderFormFieldsWithoutSaving(state, person, formFields)
  ),
  on(
    SavedTicketHolderFormActions.setSavedTicketHolderQuestionnaireFormFieldsWithoutSaving,
    (state, { person, formFields }) => setSavedTicketHolderFormFieldsWithoutSaving(state, person, formFields, true)
  ),
  on(FormFieldSyncActions.setSavedTicketHolderFormFields, (state, { person, formFields }) =>
    setSavedTicketHolderFormFields(state, person, formFields, FormName.SavedTicketHolders)
  ),
  on(FormFieldSyncActions.setCreateTicketHolderFormFields, (state, { formFields }) => {
    if (isEmpty(formFields)) {
      return state;
    }

    const { forms } = state;

    return {
      ...state,
      forms: {
        ...forms,
        [FormName.CreateTicketHolder]: {
          ...forms[FormName.CreateTicketHolder],
          formFields: {
            ...forms[FormName.CreateTicketHolder].formFields,
            ...formFields
          }
        }
      }
    };
  }),
  on(SavedTicketHolderFormActions.resetSavedTicketHoldersUnsavedFormFields, (state) => {
    const savedTicketHolders = cloneDeep(state.savedTicketHolders);

    if (isNil(savedTicketHolders)) {
      return state;
    }

    const updatedSavedTicketHolders = reduce(
      savedTicketHolders,
      (updatedSavedBuyerTicketHolders, savedBuyerTicketHolder, personUuid) => {
        updatedSavedBuyerTicketHolders[personUuid] = {
          ...savedBuyerTicketHolder,
          formFields: omitBy(savedBuyerTicketHolder.formFields, (formField) => !formField?.isSaved)
        };

        return updatedSavedBuyerTicketHolders;
      },
      {}
    );

    return {
      ...state,
      savedTicketHolders: updatedSavedTicketHolders
    };
  }),
  on(CreateTicketHolderFormActions.createTicketHolder, (state) => ({
    ...state,
    forms: {
      ...state.forms,
      [FormName.CreateTicketHolder]: {
        formFields: {}
      }
    }
  })),
  on(SavedTicketHolderActions.resetReducer, () => initialSavedTicketsHolderState)
);
